import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { Sidebar, ADMIN_PATH_FEATURE } from './components/Sidebar';
import { useAuth, type AdminUser } from './hooks/useAuth';
import { login as apiLogin, mfaChallenge, setToken, setNavigate, get as apiGet } from './services/api';
import { saveFeatures } from './utils/featureFlags';
import { pushSupported, enablePush } from './services/push';
import DashboardPage from './pages/DashboardPage';
import GpsPage from './pages/GpsPage';
import VesselsPage from './pages/VesselsPage';
import UsersPage from './pages/UsersPage';
import TalentPage from './pages/TalentPage';
import CrewSimPage from './pages/CrewSimPage';
import DocumentsPage from './pages/DocumentsPage';
import CrewAssignPage from './pages/CrewAssignPage';
import AttendancePage from './pages/AttendancePage';
import AlertsPage from './pages/AlertsPage';
import ReportsPage from './pages/ReportsPage';
import MonthlyPage from './pages/MonthlyPage';
import SyncLogsPage from './pages/SyncLogsPage';
import CertificatesPage from './pages/CertificatesPage';
import { DevicesPage } from './pages/DevicesPage';
import MessagesPage from './pages/MessagesPage';
import ChatWidget from './components/ChatWidget';
import MissingPositionBanner from './components/MissingPositionBanner';
import PositionPermissionsPage from './pages/PositionPermissionsPage';
import SettingsPage from './pages/SettingsPage';
import ManualPage from './pages/ManualPage';
import ActivityMasterPage from './pages/ActivityMasterPage';
import RestHoursPage from './pages/RestHoursPage';
import CrewDeploymentPage from './pages/CrewDeploymentPage';
import LeavePage from './pages/LeavePage';
import BoardingPage from './pages/BoardingPage';
import IncidentsPage from './pages/IncidentsPage';
import CrewCertificatesPage from './pages/CrewCertificatesPage';
import AuditLogsPage from './pages/AuditLogsPage';
import PortalLayout from './pages/portal/PortalLayout';
import PortalHomePage from './pages/portal/PortalHomePage';
import PortalBoardPage from './pages/portal/PortalBoardPage';
import PortalChatPage from './pages/portal/PortalChatPage';
import PortalManualsPage from './pages/portal/PortalManualsPage';
import PortalExpensesPage from './pages/portal/PortalExpensesPage';
import PortalRequestsPage from './pages/portal/PortalRequestsPage';
import PortalOfficePage from './pages/portal/PortalOfficePage';
import PortalCompDaysPage from './pages/portal/PortalCompDaysPage';
import PortalMyPage from './pages/portal/PortalMyPage';
import { LOGIN_ART, LOGIN_SUBTITLE, isDemoHost } from './utils/brand';

/** URLサブドメイン（ローカルは環境変数フォールバック） */
function resolveSubdomain(): string {
  const h = window.location.hostname;
  if (h !== 'localhost' && h !== '127.0.0.1') {
    const p = h.split('.');
    if (p.length >= 3 && p[0]) return p[0];
  }
  return (import.meta.env.VITE_COMPANY_SUBDOMAIN as string | undefined) ?? 'unison-tech';
}

function LoginForm({ onLoginSuccess }: { onLoginSuccess: (user: Record<string, unknown>, accessToken: string) => void }) {
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  // 会社ブランディング（運営管理者が設定した会社ロゴ）。未設定はUNISONロゴ表示
  const [brandLogo, setBrandLogo] = useState<string | null>(null);
  useEffect(() => {
    const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:3001/api';
    fetch(`${apiBase}/branding?subdomain=${encodeURIComponent(resolveSubdomain())}`)
      .then(r => (r.ok ? r.json() : null))
      .then(j => { if (j?.data?.logo_url) setBrandLogo(j.data.logo_url); })
      .catch(() => { /* 未設定・オフライン時は既定ロゴ */ });
  }, []);

  // MFA ステート
  const [step, setStep] = useState<'login' | 'mfa'>('login');
  const [mfaToken, setMfaToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [trustIp, setTrustIp] = useState(true);

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      const result = await apiLogin(userId, password);
      if ('mfa_required' in result) {
        setMfaToken(result.mfa_token);
        setStep('mfa');
      } else {
        setToken(result.data.access_token);
        onLoginSuccess(result.data.user, result.data.access_token);
      }
    } catch (e) {
      // サーバーが理由を返しているならそのまま見せる（回数制限・権限不足など）。
      // 一律「パスワードが正しくありません」にすると原因の切り分けができない。
      const m = e instanceof Error ? e.message : '';
      setErr(m && !/^HTTP \d+$/.test(m) ? m : 'ユーザーIDまたはパスワードが正しくありません');
    } finally {
      setBusy(false);
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      const data = await mfaChallenge(mfaToken, mfaCode, trustIp);
      onLoginSuccess(data.user, data.access_token);
    } catch {
      setErr('認証コードが正しくありません。再度お試しください');
    } finally {
      setBusy(false);
    }
  };

  const leftPanel = (
    <div style={{ flex: 1, position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <img src={LOGIN_ART} alt="ship" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      {/* グラデーションオーバーレイ */}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(15,23,42,0.75) 0%, rgba(2,132,199,0.45) 100%)' }} />

      {/* ロゴ（体験デモは製品名「カツオ勤怠」。会社ロゴ設定があれば白チップで表示。未設定はUNISONロゴ） */}
      <div style={{ position: 'relative', zIndex: 1, padding: '36px 40px' }}>
        {isDemoHost() ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <img src="/katsuo-mascot.png" alt="" style={{ height: 44, objectFit: 'contain' }} />
            <span style={{ fontSize: 26, fontWeight: 800, lineHeight: 1, letterSpacing: '0.02em', color: '#ffffff' }}>
              カツオ<span style={{ color: '#93b9da' }}>勤怠</span>
            </span>
          </span>
        ) : brandLogo ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', background: 'rgba(255,255,255,0.94)', borderRadius: 12, padding: '8px 18px' }}>
            <img src={brandLogo} alt="logo" style={{ height: 34, objectFit: 'contain', maxWidth: 220 }} />
          </span>
        ) : (
          <img src="/unison-logo.png" alt="UNISON" style={{ height: 36 }}
            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        )}
      </div>

      {/* キャッチコピー */}
      <div style={{ position: 'relative', zIndex: 1, marginTop: 'auto', padding: '0 40px 48px' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(14,165,233,0.25)', border: '1px solid rgba(14,165,233,0.4)', borderRadius: 999, padding: '5px 14px', marginBottom: 20 }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#38bdf8', display: 'inline-block' }} />
          <span style={{ color: '#7dd3fc', fontSize: 12, fontWeight: 600, letterSpacing: '0.05em' }}>Management Console</span>
        </div>
        <h1 style={{ margin: 0, fontSize: 34, fontWeight: 900, color: '#fff', lineHeight: 1.25, letterSpacing: '-0.5px', textShadow: '0 2px 16px rgba(0,0,0,0.4)' }}>
          船員勤怠管理<br />システム
        </h1>
        <p style={{ margin: '12px 0 0', color: 'rgba(255,255,255,0.55)', fontSize: 13, fontWeight: 500 }}>
          {LOGIN_SUBTITLE}
        </p>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', width: '100vw', height: '100vh', fontFamily: "'Inter','Hiragino Sans','Hiragino Kaku Gothic ProN','Yu Gothic',sans-serif", background: '#0f172a' }}>
      {leftPanel}

      {/* 右パネル */}
      <div style={{
        width: 440, flexShrink: 0,
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        background: '#fff', padding: '56px 44px',
      }}>

        {step === 'login' && (
          <>
            <div style={{ marginBottom: 40 }}>
              <p style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 600, color: '#0ea5e9', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Sign In</p>
              <h2 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.3px' }}>管理者ログイン</h2>
              <p style={{ margin: '8px 0 0', fontSize: 14, color: '#64748b' }}>アカウント情報を入力してください</p>
            </div>

            <form onSubmit={handleLoginSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* ユーザーID */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 7 }}>ユーザーID</label>
                <input
                  type="text" value={userId} onChange={e => setUserId(e.target.value)}
                  placeholder="例: admin" required autoComplete="username"
                  style={{ width: '100%', padding: '13px 16px', border: '1.5px solid #e2e8f0', borderRadius: 12, fontSize: 14, color: '#0f172a', outline: 'none', boxSizing: 'border-box', background: '#f8fafc', transition: 'border-color .15s' }}
                  onFocus={e => { e.target.style.borderColor = '#0ea5e9'; e.target.style.background = '#fff'; }}
                  onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.background = '#f8fafc'; }}
                />
              </div>

              {/* パスワード */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 7 }}>パスワード</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="パスワードを入力" required autoComplete="current-password"
                    style={{ width: '100%', padding: '13px 46px 13px 16px', border: '1.5px solid #e2e8f0', borderRadius: 12, fontSize: 14, color: '#0f172a', outline: 'none', boxSizing: 'border-box', background: '#f8fafc', transition: 'border-color .15s' }}
                    onFocus={e => { e.target.style.borderColor = '#0ea5e9'; e.target.style.background = '#fff'; }}
                    onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.background = '#f8fafc'; }}
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)}
                    aria-label={showPw ? 'パスワードを隠す' : 'パスワードを表示'}
                    style={{ position: 'absolute', right: 13, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4, display: 'flex', alignItems: 'center', borderRadius: 6 }}>
                    {showPw ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>

              {/* エラー */}
              {err && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '11px 14px', fontSize: 13, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>⚠️</span><span>{err}</span>
                </div>
              )}

              {/* ログインボタン */}
              <button type="submit" disabled={busy}
                style={{
                  width: '100%', padding: '14px', borderRadius: 12, border: 'none',
                  cursor: busy ? 'not-allowed' : 'pointer',
                  background: busy ? '#94a3b8' : 'linear-gradient(135deg, #0ea5e9 0%, #0369a1 100%)',
                  color: '#fff', fontSize: 15, fontWeight: 700,
                  boxShadow: busy ? 'none' : '0 4px 16px rgba(14,165,233,0.35)',
                  transition: 'all .2s', letterSpacing: '0.02em',
                }}
                onMouseEnter={e => { if (!busy) (e.currentTarget.style.transform = 'translateY(-1px)'); }}
                onMouseLeave={e => { (e.currentTarget.style.transform = 'translateY(0)'); }}
              >
                {busy ? '認証中...' : 'ログイン →'}
              </button>
            </form>
          </>
        )}

        {step === 'mfa' && (
          <>
            <div style={{ marginBottom: 32 }}>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(135deg, #0ea5e9, #0369a1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, marginBottom: 16 }}>
                🔐
              </div>
              <p style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 600, color: '#0ea5e9', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Two-Factor Auth</p>
              <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.3px' }}>二段階認証</h2>
              <p style={{ margin: '8px 0 0', fontSize: 14, color: '#64748b' }}>認証アプリに表示されている6桁のコードを入力してください</p>
            </div>

            <form onSubmit={handleMfaSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* 認証コード */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 7 }}>認証コード（6桁）</label>
                <input
                  type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                  value={mfaCode} onChange={e => setMfaCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000" required autoComplete="one-time-code" autoFocus
                  style={{ width: '100%', padding: '13px 16px', border: '1.5px solid #e2e8f0', borderRadius: 12, fontSize: 22, fontWeight: 700, color: '#0f172a', outline: 'none', boxSizing: 'border-box', background: '#f8fafc', letterSpacing: '0.3em', textAlign: 'center', transition: 'border-color .15s' }}
                  onFocus={e => { e.target.style.borderColor = '#0ea5e9'; e.target.style.background = '#fff'; }}
                  onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.background = '#f8fafc'; }}
                />
              </div>

              {/* このIPを信頼 */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13, color: '#374151' }}>
                <input type="checkbox" checked={trustIp} onChange={e => setTrustIp(e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: '#0ea5e9', cursor: 'pointer' }} />
                このIPアドレスを30日間信頼する
              </label>

              {/* エラー */}
              {err && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '11px 14px', fontSize: 13, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>⚠️</span><span>{err}</span>
                </div>
              )}

              {/* 確認ボタン */}
              <button type="submit" disabled={busy || mfaCode.length !== 6}
                style={{
                  width: '100%', padding: '14px', borderRadius: 12, border: 'none',
                  cursor: (busy || mfaCode.length !== 6) ? 'not-allowed' : 'pointer',
                  background: (busy || mfaCode.length !== 6) ? '#94a3b8' : 'linear-gradient(135deg, #0ea5e9 0%, #0369a1 100%)',
                  color: '#fff', fontSize: 15, fontWeight: 700,
                  boxShadow: (busy || mfaCode.length !== 6) ? 'none' : '0 4px 16px rgba(14,165,233,0.35)',
                  transition: 'all .2s', letterSpacing: '0.02em',
                }}
              >
                {busy ? '確認中...' : '確認する →'}
              </button>

              {/* 戻るリンク */}
              <button type="button" onClick={() => { setStep('login'); setErr(''); setMfaCode(''); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: 13, textDecoration: 'underline', padding: 0, textAlign: 'center' }}>
                ← ログイン画面に戻る
              </button>
            </form>
          </>
        )}

        {/* フッター */}
        <p style={{ marginTop: 'auto', paddingTop: 40, textAlign: 'center', fontSize: 11, color: '#cbd5e1' }}>
          © 2026 UNISON Technology. All rights reserved.
        </p>
      </div>
    </div>
  );
}

interface AlertNotif { id: string; alert_type: string; severity: string; message: string; user_name?: string; at: string; }

const SEV_COLORS: Record<string, { bg: string; border: string; color: string }> = {
  critical: { bg: '#fef2f2', border: '#f87171', color: '#dc2626' },
  warning:  { bg: '#fffbeb', border: '#fbbf24', color: '#b45309' },
};

function AlertToast({ notif, onDismiss }: { notif: AlertNotif; onDismiss: () => void }) {
  const c = SEV_COLORS[notif.severity] ?? { bg: '#f0f9ff', border: '#7dd3fc', color: '#0369a1' };
  useEffect(() => {
    const t = setTimeout(onDismiss, 8000);
    return () => clearTimeout(t);
  }, [onDismiss]);
  return (
    <div style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 10, padding: '10px 14px', boxShadow: '0 4px 16px rgba(0,0,0,0.13)', maxWidth: 340, cursor: 'pointer', position: 'relative' }} onClick={onDismiss}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <span style={{ fontSize: 18, lineHeight: 1.2 }}>{notif.severity === 'critical' ? '🚨' : '⚠️'}</span>
        <div>
          {notif.user_name && <div style={{ fontWeight: 700, fontSize: 13, color: c.color }}>{notif.user_name}</div>}
          <div style={{ fontSize: 12, color: '#374151', marginTop: 2 }}>{notif.message}</div>
        </div>
        <button onClick={onDismiss} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#9ca3af', padding: 0, flexShrink: 0 }}>✕</button>
      </div>
    </div>
  );
}

function MainLayout({ onLogout, adminUser }: { onLogout: () => void; adminUser: AdminUser & { name: string; role: string } }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isGps      = location.pathname === '/gps';
  const isMessages = location.pathname === '/messages' || location.pathname.startsWith('/portal/chat');
  // ポータル配下はCATMSのサイドバーを出さず、専用レイアウト（Viva風）で全画面表示する
  const isPortal   = location.pathname.startsWith('/portal');

  useEffect(() => {
    setNavigate(navigate);
    return () => setNavigate(() => {});
  }, [navigate]);

  // 会社ごとの機能フラグを取得してキャッシュ（新機能の出し分けに使う）
  useEffect(() => {
    apiGet<any>('/auth/features').then(r => {
      const features = r?.data ?? {};
      saveFeatures(features);
      // サイドバー等に反映させる（localStorageだけだと再描画されないため）
      try { window.dispatchEvent(new Event('catms-features-updated')); } catch { /* noop */ }
      // ポータルが有効な会社は、ログイン直後の着地をポータルホームにする（このタブで1回だけ）
      if (features.portal === true && window.location.pathname === '/dashboard' && !sessionStorage.getItem('portal_landed')) {
        sessionStorage.setItem('portal_landed', '1');
        navigate('/portal', { replace: true });
      }
      // 通知が許可済みのPCは購読をサーバーへ取り直す（許可のポップアップは出さない）
      if (features.portal === true && pushSupported() && Notification.permission === 'granted') {
        void enablePush().catch(() => { /* noop */ });
      }
    }).catch(() => { /* 前回値で判定 */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 役職ベースの制限付き管理者: 許可外ページへ直接アクセスしたらダッシュボードへ戻す
  const restricted = !['system_admin', 'shore_admin'].includes(adminUser.user_role ?? '');
  const [allowedFeat, setAllowedFeat] = useState<Record<string, boolean> | null>(null);
  useEffect(() => {
    if (!restricted) return;
    apiGet<any>('/position-permissions/my').then(r => setAllowedFeat(r?.data ?? {})).catch(() => setAllowedFeat({}));
  }, [restricted]);
  useEffect(() => {
    if (!restricted || !allowedFeat) return;
    const key = Object.keys(ADMIN_PATH_FEATURE).find(k => location.pathname === k || location.pathname.startsWith(k + '/'));
    const feat = key ? ADMIN_PATH_FEATURE[key] : undefined;
    if (feat && (feat === '_adminOnly' || !allowedFeat[feat])) navigate('/dashboard', { replace: true });
  }, [location.pathname, allowedFeat, restricted, navigate]);

  // リアルタイム通知（SSE）
  const [toasts, setToasts] = useState<AlertNotif[]>([]);
  const dismissToast = useCallback((id: string) => setToasts(ts => ts.filter(t => t.id !== id)), []);
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    const isAdmin = ['system_admin', 'shore_admin'].includes(adminUser.user_role ?? '');
    if (!isAdmin) return;
    const token = sessionStorage.getItem('admin_access_token') ?? '';
    const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:3001/api';
    const subdomain = (() => { const h = window.location.hostname; if (h !== 'localhost' && h !== '127.0.0.1') { const p = h.split('.'); if (p.length >= 3 && p[0]) return p[0]; } return (import.meta.env.VITE_COMPANY_SUBDOMAIN as string | undefined) ?? 'unison-tech'; })();
    const url = `${apiBase}/notifications/sse?token=${encodeURIComponent(token)}&subdomain=${encodeURIComponent(subdomain)}`;
    const es = new EventSource(url);
    esRef.current = es;
    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === 'connected') return;
        const notif: AlertNotif = { id: `${Date.now()}-${Math.random()}`, ...data };
        setToasts(ts => [notif, ...ts].slice(0, 5));
      } catch { /* ignore */ }
    };
    es.onerror = () => { es.close(); esRef.current = null; };
    return () => { es.close(); esRef.current = null; };
  }, [adminUser.user_role]);

  // デザイン設定をCSSカスタムプロパティに適用
  useEffect(() => {
    const apply = () => {
      try {
        const d = JSON.parse(localStorage.getItem('admin_design') ?? '{}');
        if (d.primary_color) document.documentElement.style.setProperty('--color-primary', d.primary_color);
      } catch { /* ignore */ }
    };
    apply();
    window.addEventListener('design-updated', apply);
    return () => window.removeEventListener('design-updated', apply);
  }, []);

  // ログイン直後に会社プロフィールからデザイン（ロゴ・テーマ色）を同期。
  // 運営管理者が設定した会社ロゴが、設定画面を開かなくてもサイドバーに反映される。
  useEffect(() => {
    (async () => {
      try {
        const res = await apiGet<any>('/users/me/profile');
        const d = res?.data ?? {};
        const stored = JSON.parse(localStorage.getItem('admin_design') ?? '{}');
        const next = {
          ...stored,
          primary_color: d.primary_color ?? stored.primary_color ?? null,
          logo_url: d.logo_url ?? stored.logo_url ?? null,
        };
        localStorage.setItem('admin_design', JSON.stringify(next));
        window.dispatchEvent(new Event('design-updated'));
      } catch { /* オフライン等は無視 */ }
    })();
  }, []);

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {!isPortal && <Sidebar onLogout={onLogout} userName={adminUser.user_name} isSuperAdmin={adminUser.is_super_admin} userRole={adminUser.user_role} />}
      <div style={{
        flex: 1,
        overflow: isGps ? 'hidden' : 'auto',
        background: '#F4F6F8',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {!isPortal && <MissingPositionBanner canEdit={['system_admin', 'shore_admin'].includes(adminUser.user_role ?? '')} />}
        <Routes>
          <Route path="/dashboard"    element={<DashboardPage user={adminUser} />} />
          <Route path="/gps"          element={<GpsPage />} />
          <Route path="/vessels"      element={<VesselsPage />} />
          <Route path="/users"        element={<UsersPage />} />
          <Route path="/talent"       element={<TalentPage />} />
          <Route path="/crew-sim"     element={<CrewSimPage />} />
          <Route path="/crew-certificates" element={<CrewCertificatesPage />} />
          <Route path="/documents"    element={<DocumentsPage />} />
          <Route path="/crew-assign"  element={<CrewAssignPage />} />
          <Route path="/attendance"   element={<AttendancePage />} />
          <Route path="/alerts"       element={<AlertsPage />} />
          <Route path="/messages"     element={<MessagesPage />} />
          <Route path="/reports"      element={<ReportsPage />} />
          <Route path="/monthly"      element={<MonthlyPage />} />
          <Route path="/sync-logs"           element={<SyncLogsPage />} />
          <Route path="/audit-logs"          element={<AuditLogsPage />} />
          <Route path="/position-permissions" element={<PositionPermissionsPage />} />
          <Route path="/certificates"         element={<CertificatesPage />} />
          <Route path="/hire-contract"        element={<Navigate to="/certificates" replace />} />
          <Route path="/devices"      element={<DevicesPage />} />
          <Route path="/settings"                element={<SettingsPage user={adminUser} />} />
          <Route path="/manual"                  element={<ManualPage />} />
          <Route path="/master/activities"       element={<ActivityMasterPage />} />
          <Route path="/rest-hours"   element={<RestHoursPage />} />
          <Route path="/crew-deployment" element={<CrewDeploymentPage />} />
          <Route path="/leave"        element={<LeavePage />} />
          <Route path="/boarding"     element={<BoardingPage />} />
          <Route path="/incidents"    element={<IncidentsPage />} />
          {/* カツオポータル（機能フラグ portal の会社のみ。Viva風の専用レイアウトで表示） */}
          <Route path="/portal"           element={<PortalHomePage />} />
          <Route path="/portal/board"     element={<PortalBoardPage />} />
          <Route path="/portal/chat"      element={<PortalLayout wide><PortalChatPage /></PortalLayout>} />
          <Route path="/portal/manuals"   element={<PortalLayout><PortalManualsPage /></PortalLayout>} />
          <Route path="/portal/expenses"  element={<PortalLayout><PortalExpensesPage /></PortalLayout>} />
          <Route path="/portal/requests"  element={<PortalLayout><PortalRequestsPage /></PortalLayout>} />
          <Route path="/portal/office"    element={<PortalLayout><PortalOfficePage /></PortalLayout>} />
          <Route path="/portal/comp-days" element={<PortalLayout><PortalCompDaysPage /></PortalLayout>} />
          <Route path="/portal/profile"   element={<PortalLayout><PortalMyPage /></PortalLayout>} />
          {/* 既存のユーザー管理・書類作成をポータルのレイアウトでそのまま使う（機能は共通） */}
          <Route path="/portal/users"        element={<PortalLayout wide><UsersPage /></PortalLayout>} />
          <Route path="/portal/certificates" element={<PortalLayout wide><CertificatesPage /></PortalLayout>} />
          {/* 労務管理記録簿は証明書発行ページ（月次労働記録 → 様式第十六号の五）に統合 */}
          <Route path="/labor-records" element={<Navigate to="/certificates" replace />} />
          <Route path="/master/overtime-rules"   element={<Navigate to="/master/activities" replace />} />
          <Route path="*"             element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </div>
      {!isMessages && !isPortal && <ChatWidget />}
      {toasts.length > 0 && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8, pointerEvents: 'none' }}>
          {toasts.map(t => (
            <div key={t.id} style={{ pointerEvents: 'auto' }}>
              <AlertToast notif={t} onDismiss={() => dismissToast(t.id)} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function App() {
  const { user, loading, loginWithToken, logout } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0f172a', color: '#38bdf8', fontSize: 18 }}>
        読み込み中...
      </div>
    );
  }

  const handleLoginSuccess = (userData: Record<string, unknown>, accessToken: string) => {
    loginWithToken(accessToken, userData);
  };

  if (!user) {
    return <LoginForm onLoginSuccess={handleLoginSuccess} />;
  }

  const adminUser = {
    id: user.id,
    name: user.user_name ?? 'Admin',
    role: user.user_role ?? 'admin',
    company_id: user.company_id,
    user_name: user.user_name,
    user_role: user.user_role,
    is_super_admin: user.is_super_admin,
  };

  return (
    <BrowserRouter>
      <MainLayout onLogout={logout} adminUser={adminUser} />
    </BrowserRouter>
  );
}

export default App;
